import { useQuery } from '@apollo/client';
import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { _columns } from 'common/components/ActionMenu/Attach/_columns.data';
import { useStyles } from 'common/components/ActionMenu/Attach/AttachFormModal/index.styles';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import { TABLE_ROWS } from 'common/constants';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback } from 'react';
import {
  TravelAuthorizationAvailableDocumentTypes,
  TravelAuthorizationAvailableDocumentTypesVariables,
} from '../../__generated__/TravelAuthorizationAvailableDocumentTypes';
import { toOrderByVariable } from '../utils';
import {
  AttachableTravelAuthorizationDocuments,
  AttachableTravelAuthorizationDocuments_attachableTravelAuthorizationDocuments_nodes,
} from './__generated__/AttachableTravelAuthorizationDocuments';
import { AttachTravelAuthorizationDocumentVariables } from './__generated__/AttachTravelAuthorizationDocument';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { TravelAuthorization_travelAuthorization } from 'travelAuthorization/TravelPlan/view/__generated__/TravelAuthorization';

import ATTACHABLE_TRAVEL_AUTHORIZATION_DOCUMENTS from './AttachableTravelAuthorizationDocuments.graphql';
import ATTACH_TRAVEL_AUTHORIZATION_DOCUMENT from './AttachTravelAuthorizationDocument.graphql';
import TRAVEL_AUTHORIZATION from '../../../../../TravelAuthorization.graphql';
import TRAVEL_AUTHORIZATION_AVAILABLE_DOCUMENT_TYPES from '../../TravelAuthorizationAvailableDocumentTypes.graphql';

type AttachableDataType =
  AttachableTravelAuthorizationDocuments_attachableTravelAuthorizationDocuments_nodes;
interface AttachFormModalProps {
  travelAuthorizationData: TravelAuthorization_travelAuthorization;
  isDirty: boolean;
}

export const AttachFormModal: React.FC<AttachFormModalProps> = ({
  travelAuthorizationData,
}) => {
  const styles = useStyles();
  const { id, isDraft } = { ...travelAuthorizationData };

  const { data: availableDocumentTypes } = useQuery<
    TravelAuthorizationAvailableDocumentTypes,
    TravelAuthorizationAvailableDocumentTypesVariables
  >(TRAVEL_AUTHORIZATION_AVAILABLE_DOCUMENT_TYPES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const getDocumentTypeOptions = useCallback(
    (data: TravelAuthorizationAvailableDocumentTypes | undefined) => {
      return (
        data?.travelAuthorizationAvailableDocumentTypes?.nodes.map(
          (doctype) => ({
            key: doctype.id,
            text: doctype.documentType || '',
            title:
              !isDraft && doctype.isAccountingDocument
                ? 'Upload prohibited during/after the approval process'
                : undefined,
            disabled:
              !isDraft && doctype.isAccountingDocument ? true : undefined,
          })
        ) || []
      );
    },
    [isDraft]
  );

  const canSelectItem = useCallback(
    (item: AttachableDataType) =>
      isDraft ? true : !item._isAccountingDocument!,
    [isDraft]
  );

  const transformData = useCallback(
    (data: AttachableTravelAuthorizationDocuments | undefined) => {
      return data?.attachableTravelAuthorizationDocuments?.nodes || [];
    },
    []
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableDataType[], entityId: string) => {
      const entityDocIds = selectedItems.map((item) => item.id);
      return {
        input: {
          travelAuthorizationId: entityId,
          entityDocumentId: entityDocIds,
          documentTypeId: null, // Will be set via onDocumentTypeChange
        },
      } as AttachTravelAuthorizationDocumentVariables;
    },
    []
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableDataType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;

      const disableLogic = isDraft ? false : item._isAccountingDocument;

      const fieldContent = item[
        column.fieldName as keyof AttachableDataType
      ] as string | null;
      switch (column.key) {
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout disabled={disableLogic!} item={item} />
            </Stack>
          );
        case 'documentType':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.documentTypes?.documentType}
              </Text>
            </Stack>
          );
        case 'indexAmount':
          return (
            <Stack
              verticalAlign="center"
              className={styles.onrenderColumnStack}
            >
              <Text className={styles.contentColumnAlignRight}>
                {fieldContent}
              </Text>
            </Stack>
          );
        case 'isoCode':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item.currency?.isoCode}
              </Text>
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {item._uploadDate ? getGlobalDateFormat(item._uploadDate) : ''}
              </Text>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text className={disableLogic ? styles.disabledText : ''}>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text className={disableLogic ? styles.disabledText : ''}>
                {fieldContent}
              </Text>
            </Stack>
          );
      }
    },
    [styles, isDraft]
  );

  return (
    <GenericAttachForm
      entity={{
        id: id!,
        isDraft,
      }}
      availableDocumentTypes={availableDocumentTypes}
      attachableDocumentsQuery={ATTACHABLE_TRAVEL_AUTHORIZATION_DOCUMENTS}
      attachDocumentsMutation={ATTACH_TRAVEL_AUTHORIZATION_DOCUMENT}
      detailsQuery={TRAVEL_AUTHORIZATION}
      columns={_columns}
      modalWidth={1350}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      canSelectItem={canSelectItem}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{
        travelAuthorizationId: id!,
        first: TABLE_ROWS,
      }}
      toOrderByVariable={toOrderByVariable}
    />
  );
};
